import React, { useEffect, useRef, useState } from "react";

import {
  Headphone,
  MediaUpload
} from "../../../../Constant/api";

export default function AudioUpload(props) {
  const {
    ImageUrl,
    setImageUrl,
    MediaUploadType,
    setMediaUploadType,
    type,
    index,
    onChange
  } = props;

  const fileRefSingle = useRef(null);
  const audioRef = useRef(null);

  const [Loading, setLoading] = useState(false);

  const [isDragging, setIsDragging] = useState(false);

  // Local selected file
  const [selectedFile, setSelectedFile] = useState(null);

  // Local browser preview URL
  const [localAudioUrl, setLocalAudioUrl] = useState("");

  // Audio duration
  const [duration, setDuration] = useState(0);

  // Trim positions
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);

  // Current playing time
  const [currentTime, setCurrentTime] = useState(0);

  /*
   * Cleanup local URL
   */
  useEffect(() => {
    return () => {
      if (localAudioUrl) {
        URL.revokeObjectURL(localAudioUrl);
      }
    };
  }, [localAudioUrl]);

  /*
   * Convert seconds to MM:SS
   */
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) {
      return "00:00";
    }

    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  /*
   * Prepare local audio
   */
  const prepareAudio = (file) => {
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      alert("Please select an audio file.");
      return;
    }

    if (localAudioUrl) {
      URL.revokeObjectURL(localAudioUrl);
    }

    const localUrl =
      URL.createObjectURL(file);

    setSelectedFile(file);
    setLocalAudioUrl(localUrl);

    setDuration(0);
    setStartTime(0);
    setEndTime(0);
    setCurrentTime(0);

    if (setMediaUploadType) {
      setMediaUploadType("audio");
    }
  };

  /*
   * File select
   */
  const handleFileSelect = (event) => {
    const file =
      event.target.files?.[0];
    if (!file) return;
    prepareAudio(file);
  };

  /*
   * Drag handlers
   */
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragging(false);

    const file =
      event.dataTransfer.files?.[0];

    if (!file) return;

    prepareAudio(file);
  };

  /*
   * When audio metadata loads,
   * get total duration
   */
  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;

    const audioDuration =
      audioRef.current.duration;

    if (!isFinite(audioDuration)) {
      return;
    }

    setDuration(audioDuration);

    setStartTime(0);

    setEndTime(audioDuration);
  };

  /*
   * Start range change
   */
  const handleStartChange = (value) => {
    const newStart = Number(value);

    if (newStart >= endTime) {
      return;
    }

    setStartTime(newStart);

    if (audioRef.current) {
      audioRef.current.currentTime = newStart;
    }
  };

  /*
   * End range change
   */
  const handleEndChange = (value) => {
    const newEnd = Number(value);
    if (newEnd <= startTime) { return; }

    setEndTime(newEnd);
  };

  /*
   * Preview trimmed section
   */
  const previewTrim = () => {
    if (!audioRef.current) return;

    audioRef.current.currentTime = startTime;

    audioRef.current.play();
  };

  /*
   * Stop playback automatically
   * at selected end position
   */
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;

    const time = audioRef.current.currentTime;

    setCurrentTime(time);

    if (endTime > 0 && time >= endTime) {
      audioRef.current.pause();

      audioRef.current.currentTime = startTime;

      setCurrentTime(startTime);
    }
  };

  /*
   * AUDIO TRIMMING
   */

  const trimAudio = async (file, start, end) => {
    /*
     * Read local file
     */
    const arrayBuffer = await file.arrayBuffer();

    /*
     * Decode audio
     */
    const AudioContext = window.AudioContext || window.webkitAudioContext;

    const audioContext = new AudioContext();

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    /*
     * Convert seconds to audio samples
     */
    const sampleRate =
      audioBuffer.sampleRate;

    const startOffset =
      Math.floor(
        start * sampleRate
      );

    const endOffset =
      Math.floor(
        end * sampleRate
      );

    const frameCount =
      endOffset - startOffset;

    if (frameCount <= 0) {
      throw new Error(
        "Invalid trim range."
      );
    }

    /*
     * Create new trimmed AudioBuffer
     */
    const newAudioBuffer =
      audioContext.createBuffer(
        audioBuffer.numberOfChannels,
        frameCount,
        sampleRate
      );

    /*
     * Copy selected samples
     */
    for (
      let channel = 0;
      channel <
      audioBuffer.numberOfChannels;
      channel++
    ) {
      const oldChannel =
        audioBuffer.getChannelData(
          channel
        );

      const newChannel =
        newAudioBuffer.getChannelData(
          channel
        );

      for (
        let i = 0;
        i < frameCount;
        i++
      ) {
        newChannel[i] =
          oldChannel[
          i + startOffset
          ];
      }
    }

    /*
     * Convert AudioBuffer to WAV Blob
     */
    const wavBlob =
      audioBufferToWav(
        newAudioBuffer
      );

    await audioContext.close();

    /*
     * Create actual File
     */
    const trimmedFile =
      new File(
        [wavBlob],
        `trimmed-audio-${Date.now()}.wav`,
        {
          type: "audio/wav"
        }
      );

    return trimmedFile;
  };

  /*
   * =============================
   *
   * AUDIO BUFFER -> WAV
   *
   * =============================
   */

  const audioBufferToWav = (
    buffer
  ) => {
    const numberOfChannels =
      buffer.numberOfChannels;

    const sampleRate =
      buffer.sampleRate;

    const format = 1;

    const bitDepth = 16;

    /*
     * Combine channels
     */
    let samples;

    if (numberOfChannels === 2) {
      samples = interleave(
        buffer.getChannelData(0),
        buffer.getChannelData(1)
      );
    } else {
      samples =
        buffer.getChannelData(0);
    }

    const blockAlign =
      numberOfChannels *
      bitDepth /
      8;

    const byteRate =
      sampleRate *
      blockAlign;

    const dataLength =
      samples.length * 2;

    const arrayBuffer =
      new ArrayBuffer(
        44 + dataLength
      );

    const view =
      new DataView(arrayBuffer);

    /*
     * RIFF
     */
    writeString(
      view,
      0,
      "RIFF"
    );

    view.setUint32(
      4,
      36 + dataLength,
      true
    );

    writeString(
      view,
      8,
      "WAVE"
    );

    /*
     * fmt
     */
    writeString(
      view,
      12,
      "fmt "
    );

    view.setUint32(
      16,
      16,
      true
    );

    view.setUint16(
      20,
      format,
      true
    );

    view.setUint16(
      22,
      numberOfChannels,
      true
    );

    view.setUint32(
      24,
      sampleRate,
      true
    );

    view.setUint32(
      28,
      byteRate,
      true
    );

    view.setUint16(
      32,
      blockAlign,
      true
    );

    view.setUint16(
      34,
      bitDepth,
      true
    );

    /*
     * data
     */
    writeString(
      view,
      36,
      "data"
    );

    view.setUint32(
      40,
      dataLength,
      true
    );

    /*
     * PCM data
     */
    floatTo16BitPCM(
      view,
      44,
      samples
    );

    return new Blob(
      [view],
      {
        type: "audio/wav"
      }
    );
  };

  /*
   * Stereo channels ->
   * one PCM stream
   */
  const interleave = (
    left,
    right
  ) => {
    const length =
      left.length +
      right.length;

    const result =
      new Float32Array(
        length
      );

    let inputIndex = 0;

    for (
      let index = 0;
      index < length;
    ) {
      result[index++] =
        left[inputIndex];

      result[index++] =
        right[inputIndex];

      inputIndex++;
    }

    return result;
  };

  /*
   * Write string to WAV header
   */
  const writeString = (
    view,
    offset,
    string
  ) => {
    for (
      let i = 0;
      i < string.length;
      i++
    ) {
      view.setUint8(
        offset + i,
        string.charCodeAt(i)
      );
    }
  };

  /*
   * Float audio ->
   * signed 16-bit PCM
   */
  const floatTo16BitPCM = (
    output,
    offset,
    input
  ) => {
    for (
      let i = 0;
      i < input.length;
      i++,
      offset += 2
    ) {
      let sample =
        Math.max(
          -1,
          Math.min(
            1,
            input[i]
          )
        );

      sample =
        sample < 0
          ? sample * 0x8000
          : sample * 0x7fff;

      output.setInt16(
        offset,
        sample,
        true
      );
    }
  };

  /*
   * =============================
   *
   * TRIM AND UPLOAD
   *
   * =============================
   */

  const trimAndUploadAudio =
    async () => {
      if (!selectedFile) {
        alert(
          "Please select an audio file."
        );

        return;
      }

      if (
        endTime <= startTime
      ) {
        alert(
          "End time must be greater than start time."
        );

        return;
      }

      try {
        setLoading(true);

        /*
         * 1. Crop/trim locally
         */
        const trimmedFile =
          await trimAudio(
            selectedFile,
            startTime,
            endTime
          );

        console.log(
          "Trimmed file:",
          trimmedFile
        );

        /*
         * 2. Upload trimmed file
         */
        const response =
          await MediaUpload(
            trimmedFile
          );

        if (!response.ok) {
          throw new Error(
            "Audio upload failed."
          );
        }

        const data =
          await response.json();

        if (!data?.source_url) {
          throw new Error(
            "source_url not returned."
          );
        }

        /*
         * 3. Save URL
         */
        setImageUrl(
          data.source_url
        );

        onChange(
          type,
          data.source_url,
          index
        );

        /*
         * 4. Cleanup
         */
        if (localAudioUrl) {
          URL.revokeObjectURL(
            localAudioUrl
          );
        }

        setLocalAudioUrl("");
        setSelectedFile(null);

        setStartTime(0);
        setEndTime(0);
        setDuration(0);

        if (
          fileRefSingle.current
        ) {
          fileRefSingle.current.value =
            "";
        }

      } catch (error) {
        console.error(
          "Audio trim/upload error:",
          error
        );

        alert(
          error.message ||
          "Unable to trim audio."
        );
      } finally {
        setLoading(false);
      }
    };

  /*
   * Upload original without trimming
   */
  const uploadOriginalAudio =
    async () => {
      if (!selectedFile) {
        return;
      }

      try {
        setLoading(true);

        /*
         * Preserve original MIME
         */
        const extension =
          selectedFile.name
            .split(".")
            .pop();

        const name =
          `audio-${Date.now()}.${extension}`;

        const file =
          new File(
            [selectedFile],
            name,
            {
              type:
                selectedFile.type
            }
          );

        const response =
          await MediaUpload(
            file
          );

        if (!response.ok) {
          throw new Error(
            "Upload failed."
          );
        }

        const data =
          await response.json();

        setImageUrl(
          data.source_url
        );

        onChange(
          type,
          data.source_url,
          index
        );

        if (localAudioUrl) {
          URL.revokeObjectURL(
            localAudioUrl
          );
        }

        setLocalAudioUrl("");
        setSelectedFile(null);

      } catch (error) {
        console.error(error);

        alert(
          "Unable to upload audio."
        );
      } finally {
        setLoading(false);
      }
    };

  /*
   * Cancel selected audio
   */
  const cancelAudio = () => {
    if (localAudioUrl) {
      URL.revokeObjectURL(
        localAudioUrl
      );
    }

    setLocalAudioUrl("");
    setSelectedFile(null);

    setStartTime(0);
    setEndTime(0);
    setDuration(0);

    if (
      fileRefSingle.current
    ) {
      fileRefSingle.current.value =
        "";
    }
  };

  /*
   * Clear uploaded audio
   */
  const clearAudio = () => {
    setImageUrl("");

    onChange(
      type,
      "",
      index
    );
  };

  return (
    <div>

      {/* ========================= */}
      {/* LOADING */}
      {/* ========================= */}

      {Loading && (
        <div className="mb-2">

          <div
            className="
              spinner-border
              spinner-border-sm
            "
          />

          <span className="ms-2">
            Processing audio...
          </span>

        </div>
      )}


      {/* ========================= */}
      {/* DRAG / DROP */}
      {/* ========================= */}

      {!ImageUrl &&
        !localAudioUrl && (

          <div
            onDragEnter={
              handleDragEnter
            }

            onDragOver={
              handleDragOver
            }

            onDragLeave={
              handleDragLeave
            }

            onDrop={
              handleDrop
            }

            onClick={() =>
              fileRefSingle.current?.click()
            }

            style={{
              width: "250px",

              border:
                isDragging
                  ? "2px dashed #0d6efd"
                  : "2px dashed #aaa",

              background:
                isDragging
                  ? "#f0f7ff"
                  : "#fff",

              borderRadius:
                "8px",

              padding:
                "25px",

              textAlign:
                "center",

              cursor:
                "pointer"
            }}
          >

            <img
              src={Headphone}
              alt="Audio"
              style={{
                width: "30px",
                height: "30px"
              }}
            />

            <div
              style={{
                fontSize: "14px",
                marginTop: "6px"
              }}
            >
              Drag & Drop Audio
            </div>

            <small
              className="text-muted"
            >
              or click to select
            </small>

          </div>
        )}


      {/* ========================= */}
      {/* LOCAL AUDIO TRIMMER */}
      {/* ========================= */}

      {localAudioUrl && (

        <div
          className="
            border
            rounded
            p-3
            my-2
          "
          style={{
            maxWidth: "600px"
          }}
        >

          <h5>
            Crop Audio
          </h5>

          <div
            className="
              text-muted
              mb-2
            "
            style={{
              fontSize: "12px"
            }}
          >
            {selectedFile?.name}
          </div>


          {/* AUDIO PLAYER */}

          <audio
            ref={audioRef}

            src={localAudioUrl}

            controls

            onLoadedMetadata={
              handleLoadedMetadata
            }

            onTimeUpdate={
              handleTimeUpdate
            }

            style={{
              width: "100%"
            }}
          />


          {/* ========================= */}
          {/* START */}
          {/* ========================= */}

          <div className="mt-4">

            <div
              className="
                d-flex
                justify-content-between
              "
            >
              <strong>
                Start
              </strong>

              <span>
                {formatTime(
                  startTime
                )}
              </span>
            </div>

            <input
              type="range"

              min="0"

              max={
                duration || 0
              }

              step="0.1"

              value={
                startTime
              }

              onChange={(e) =>
                handleStartChange(
                  e.target.value
                )
              }

              className="
                form-range
              "
            />

          </div>


          {/* ========================= */}
          {/* END */}
          {/* ========================= */}

          <div className="mt-3">

            <div
              className="
                d-flex
                justify-content-between
              "
            >
              <strong>
                End
              </strong>

              <span>
                {formatTime(
                  endTime
                )}
              </span>
            </div>

            <input
              type="range"

              min="0"

              max={
                duration || 0
              }

              step="0.1"

              value={
                endTime
              }

              onChange={(e) =>
                handleEndChange(
                  e.target.value
                )
              }

              className="
                form-range
              "
            />

          </div>


          {/* ========================= */}
          {/* INFORMATION */}
          {/* ========================= */}

          <div
            className="
              mt-3
              p-2
              bg-light
              rounded
            "
          >

            <div>
              Original Duration:
              {" "}
              <strong>
                {formatTime(
                  duration
                )}
              </strong>
            </div>

            <div>
              Start:
              {" "}
              <strong>
                {formatTime(
                  startTime
                )}
              </strong>
            </div>

            <div>
              End:
              {" "}
              <strong>
                {formatTime(
                  endTime
                )}
              </strong>
            </div>

            <div>
              Cropped Duration:
              {" "}
              <strong>
                {formatTime(
                  endTime -
                  startTime
                )}
              </strong>
            </div>

          </div>


          {/* ========================= */}
          {/* PREVIEW */}
          {/* ========================= */}

          <button
            type="button"

            className="
              btn
              btn-outline-secondary
              btn-sm
              mt-3
            "

            onClick={
              previewTrim
            }
          >
            <i
              className="
                fa
                fa-play
                me-2
              "
            />

            Preview Selected Audio
          </button>


          {/* ========================= */}
          {/* BUTTONS */}
          {/* ========================= */}

          <div
            className="
              d-flex
              flex-wrap
              gap-2
              mt-4
            "
          >

            <button
              type="button"

              className="
                btn
                btn-secondary
              "

              disabled={
                Loading
              }

              onClick={
                cancelAudio
              }
            >
              Cancel
            </button>


            <button
              type="button"

              className="
                btn
                btn-outline-primary
              "

              disabled={
                Loading
              }

              onClick={
                uploadOriginalAudio
              }
            >
              Upload Original
            </button>


            <button
              type="button"

              className="
                btn
                btn-primary
              "

              disabled={
                Loading
              }

              onClick={
                trimAndUploadAudio
              }
            >

              {Loading
                ? "Processing..."
                : "Crop & Upload"
              }

            </button>

          </div>

        </div>
      )}


      {/* ========================= */}
      {/* ALREADY UPLOADED */}
      {/* ========================= */}

      {ImageUrl &&
        !localAudioUrl && (

          <div className="my-2">

            {/* <audio
              src={
                ImageUrl
              }

              controls

              style={{
                width: "100%",
                maxWidth: "500px"
              }}
            /> */}

            {/* <audio
              key={ImageUrl}
              src={ImageUrl}
              controls
              preload="metadata"
              style={{
                width: "100%",
                maxWidth: "500px"
              }}
            >
              Your browser does not support audio.
            </audio> */}

            <audio controls>
              {/* <source src={`${ImageUrl}`} type="audio/ogg" /> */}
              <source src={`${ImageUrl}`} type="audio/mpeg" />
              <source src={`${ImageUrl}`} type="audio/mp3" />
              <source src={`${ImageUrl}`} type="audio/wav" />
              <source src={`${ImageUrl}`} type="audio/ogg" />
              Your browser does not support the audio element.
            </audio>


            <div
              className="
              d-flex
              gap-3
              mt-2
            "
            >

              <button
                type="button"

                className="
                btn
                btn-link
                p-0
              "

                onClick={() =>
                  fileRefSingle.current?.click()
                }
              >
                Change
              </button>


              <button
                type="button"

                className="
                btn
                btn-link
                text-danger
                p-0
              "

                onClick={
                  clearAudio
                }
              >
                Clear
              </button>

            </div>

          </div>
        )}


      {/* ========================= */}
      {/* HIDDEN INPUT */}
      {/* ========================= */}

      <input
        ref={
          fileRefSingle
        }

        type="file"

        accept="audio/*"

        style={{
          display:
            "none"
        }}

        onChange={
          handleFileSelect
        }
      />

    </div>
  );
}